import { Icon } from '@iconify/react'
import { Computed } from '@legendapp/state/react'
import { formOptions, useForm } from '@tanstack/react-form'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-form-adapter'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { OperatorGroups } from '~features/operator-groups/types'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Card,
  CardContent,
  CardFooter,
  IconButton,
  Input,
  Label,
  Textarea,
} from '~in-app/components'
import MultiSelectDropdown, {
  Option,
} from '~in-app/components/MultiSelectDropdown'
import { auth$ } from '~in-app/legend-state'
import { fetchAllDivision, fetchOperatorGroupDetails, fetchOperatorList, fetchRolesList, updateOperatorGroup } from '~in-app/shareable/index.api'
import { operatorGroupValidation } from '~in-app/validation/operatorGroups.validation'

export const Route = createFileRoute('/console/operator-groups/edit/$id')({
  component: AddOperatorGroupPage,
  staticData: {
    privileges: ['0.3.3.2'],
  },
})

function AddOperatorGroupPage() {
  const { id } = Route.useParams()
  const router = useRouter()
  const { data: data } = useQuery({
    queryKey: ['operator-group-details'],
    queryFn: async () => {
      if (!auth$.privileges.get()?.has('0.3.5.0')) {
        return {} as OperatorGroups
      }
      const response = await fetchOperatorGroupDetails({ id })
      if (!response || !Array.isArray(response) || response.length === 0) {
        return {}
      }
      return response[0]
    },
  })

  const { data: roleList } = useQuery({
    queryKey: ['add-operator-group-role-listing'],
    queryFn: async ({ signal }) => {
      if (!auth$.privileges.get()?.has('0.3.5.0')) {
        return [] as Option[]
      }

      const response = await fetchRolesList()
      const roleList = response?.list?.map((item: any) => ({
        label: item.name,
        value: item.id,
      })) ?? []

      return roleList
    },
  })

  const { data: operatorList = [] } = useQuery({
    queryKey: ['add-operator-group-operator-listing'],
    queryFn: async ({ signal }) => {
      if (!auth$.privileges.get()?.has('0.3.2.0')) {
        return [] as Option[]
      }

      const response = await fetchOperatorList()
      const operatorList = response?.list?.map((item: any) => ({
        label: `${item?.firstName || ''} ${item?.lastName || ''}`,
        value: item.id,
      })) ?? []

      return operatorList
    },
  })

  const { data: divisionList = [] } = useQuery<Option[]>({
    queryKey: ['add-operator-group-division-listing'],
    queryFn: async ({ signal }) => {
      if (!auth$.privileges.get()?.has('0.4.0.0')) {
        return [] as Option[]
      }

      const response = await fetchAllDivision()

      const flattenDivisionsWithReduce = (
        divisions: any[],
        parentId: string | null = null,
      ): any[] => {
        return divisions.reduce((acc: any[], division: any) => {
          acc.push({
            value: division.id,
            label: division.name,
            parentId: parentId,
          })

          if (division.mychilds && division.mychilds.length > 0) {
            acc = acc.concat(
              flattenDivisionsWithReduce(division.mychilds, division.id),
            )
          }

          return acc
        }, [])
      }

      const flatDivisionList = flattenDivisionsWithReduce(response.divisions)
      return flatDivisionList
    },
  })

  const saveOperatorGroupFunc = () => {
    const mutation = useMutation({
      mutationFn: updateOperatorGroup,
      onSuccess: (response) => {
        toast.success(`Record Added Successfully`, {
          position: 'top-center',
          hideProgressBar: false,
          autoClose: 8000,
        })

        router.navigate({ to: '/console/operator-groups/list' })
      },
      onError: (error: Error) => {
        console.error('Error occurred:', error)
      },
    })

    return mutation
  }
  const saveOperatorGroupMutation = saveOperatorGroupFunc()
  const submitOperatorGroups = async (value: any) => {
    try {
      const submitValue = JSON.parse(JSON.stringify(value))
      Object.entries(submitValue).forEach(([key, value]) => {
        if (value === '') {
          delete submitValue[key]
        }
        else if (['roleIds', 'divisionIds', 'operatorIds'].includes(key)) {
          submitValue[key] = (value as any[])
            .map(item => item.value)
            .filter(Boolean)
        }
      })

      saveOperatorGroupMutation.mutate([submitValue])
    }
    catch (error) {
      console.error(error)
      toast.error(`Submit Error: ${error}`, {
        position: 'top-center',
        hideProgressBar: false,
        autoClose: 500,
      })
    }
  }

  const formOpts = formOptions({
    defaultValues: {
      name: '',
      remarks: '',
      desp: '',
      id: '',
      active: true,
      operatorIds: [] as Option[],
      divisionIds: [] as Option[],
      roleIds: [] as Option[],
    },
    validatorAdapter: zodValidator(),
    validators: {
      onChange: operatorGroupValidation,
    },
    onSubmit: async ({ value }) => {
      // Handle empty values for 'address', 'desp', and 'remarks

      try {
        submitOperatorGroups(value)
      }
      catch (error) {
        console.error('Updated Operator Group failed:', error)
        toast.error(`Error Submitting: ${error}`, {
          position: 'top-center',
          hideProgressBar: false,
          autoClose: 500,
        })
      }
    },
  })

  const formControl = useForm(formOpts)
  useEffect(() => {
    if (data) {
      const formValues = {
        name: data.name ?? '',
        remarks: data.remarks ?? '',
        desp: data.desp ?? '',
        id: data.id ?? '',
        active: data.active ?? true,
        operatorIds: data.operators?.map((operator: any) => ({ value: operator.id, label: `${operator?.firstName || ''} ${operator?.lastName || ''}` })) ?? [],
        divisionIds: data.divisions?.map((division: any) => ({ value: division.id, label: division.name })) ?? [],
        roleIds: data.roles?.map((role: any) => ({ value: role.id, label: role.name })) ?? [],
        version: data.version ?? 0,
      }
      // Set all form values at once
      Object.entries(formValues).forEach(([key, value]) => {
        formControl.setFieldValue(key as any, value)
      })
    }
  }, [data])

  const [value, setValue] = useState(['item-1'])
  useEffect(() => {
    const validateFields = async () => {
      const fieldMeta = formControl.state.fieldMeta
      if (fieldMeta && formControl.state.errors.length > 0) {
        const fieldsToValidate = Object.keys(fieldMeta) as string[]
        const validationResults = await Promise.all(
          fieldsToValidate.map(async (field) => {
            const result = await formControl.validateField(field as any, 'change')
            return { field, result }
          }),
        )

        for (const { field, result: errors } of validationResults) {
          if (errors.length > 0) {
            const fieldElement = document.getElementById(field)
            fieldElement?.focus()
            // Scroll to the field element if it exists
            fieldElement?.scrollIntoView({ behavior: 'smooth', block: 'center' })
            break
          }
        }
      }
    }

    validateFields()
  }, [formControl.state.errors])

  return (
    <div>
      <div className="p-2">
        <IconButton
          color="muted"
          onClick={() => router.history.back()}
        >
          <Icon icon="lucide:arrow-left" />
        </IconButton>

        <span className="mb-4 ml-4 text-2xl font-bold">
          Update Operating Group Page
        </span>
      </div>

      <Card className="mt-4">
        <div className="p-2">
          <form
            noValidate
            onSubmit={
              (e) => {
                e.preventDefault()
                setValue(['item-1'])
                formControl.handleSubmit()
              }
            }
          >
            <CardContent className="mt-4 grid gap-4">
              {/* Site Information */}
              <Accordion
                type="multiple"
                value={value}
                onValueChange={setValue}
              >
                <AccordionItem value="item-1">
                  <AccordionTrigger>
                    <h4 className="text-lg font-semibold">
                      Operator Groups Details
                    </h4>
                  </AccordionTrigger>

                  <AccordionContent className="w-full">
                    <div className="grid grid-cols-1 gap-1">
                      <div className="grid">
                        <formControl.Field name="name">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="operatorGroupName"
                                  >
                                    Name
                                  </Label>

                                  <Input
                                    id="name"
                                    type="text"
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={
                                      e =>
                                        field.handleChange(e.target.value)
                                    }
                                  />
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      {/* Description Field */}
                      <div className="grid gap-2">
                        <formControl.Field name="divisionIds">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="operatorGroupDivisionIds"
                                  >
                                    Divisions
                                  </Label>

                                  <Computed>
                                    {
                                      () => (
                                        <MultiSelectDropdown
                                          id="divisionIds"
                                          options={divisionList}
                                          value={field.state.value}
                                          onChange={
                                            (e) => {
                                              field.handleChange(e)
                                              field.validate('change')
                                            }
                                          }
                                        />
                                      )
                                    }
                                  </Computed>
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      {/* Remarks Field */}
                      <div className="grid gap-2">
                        <formControl.Field name="operatorIds">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="operatorGroupsOperatorIds"
                                  >
                                    Operators
                                  </Label>

                                  <Computed>
                                    {
                                      () => (
                                        <MultiSelectDropdown
                                          id="operatorIds"
                                          options={operatorList}
                                          value={field.state.value}
                                          onChange={
                                            (e) => {
                                              field.handleChange(e)
                                              field.validate('change')
                                            }
                                          }
                                        />
                                      )
                                    }
                                  </Computed>
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 ">
                      {/* Remarks Field */}
                      <div className="grid gap-2">
                        <formControl.Field name="roleIds">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="operatorGroupsRoleIds"
                                  >
                                    Roles
                                  </Label>

                                  <Computed>
                                    {
                                      () => (
                                        <MultiSelectDropdown
                                          id="roleIds"
                                          options={roleList ?? []}
                                          value={field.state.value}
                                          onChange={
                                            (e) => {
                                              field.handleChange(e)
                                              field.validate('change')
                                            }
                                          }
                                        />
                                      )
                                    }
                                  </Computed>
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      {/* Description Field */}
                      <div className="grid gap-2">
                        <formControl.Field name="desp">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    className="text-xs"
                                    htmlFor="operatorGroupDesp"
                                  >
                                    Description
                                  </Label>

                                  <Textarea
                                    id="desp"
                                    placeholder="Type your Description here."
                                    rows={2}
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={
                                      e =>
                                        field.handleChange(e.target.value)
                                    }
                                  />
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      {/* Remarks Field */}
                      <div className="grid gap-2">
                        <formControl.Field name="remarks">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    className="text-xs"
                                    htmlFor="operatorGroupRemarks"
                                  >
                                    Remarks
                                  </Label>

                                  <Textarea
                                    id="remarks"
                                    placeholder="Type your Remarks here."
                                    rows={2}
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={
                                      e =>
                                        field.handleChange(e.target.value)
                                    }
                                  />
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>

            <CardFooter>
              <formControl.Subscribe
                selector={state => [state.canSubmit, state.isSubmitting]}
              >
                {
                  ([canSubmit, isSubmitting]) => (
                    <Button
                      fullWidth
                      disabled={!canSubmit || !auth$.privileges.get()?.has('0.3.3.2')}
                    >
                      {isSubmitting ? '...' : 'Save'}
                    </Button>
                  )
                }
              </formControl.Subscribe>
            </CardFooter>
          </form>
        </div>
      </Card>
    </div>
  )
}
